import { get, isEmpty } from 'lodash-es';

// Models
import { InventoryModel, InventoryPaths } from 'client/data/models/inventory';
import { UrlModel, URL_CONTEXT_PATH } from 'client/data/models/url';
import { CmsModel } from 'client/data/models/cms';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { PageModel } from 'client/data/models/page';
import { getReduxModelStateFromStore } from 'client/data/luckdragon/redux/redux-model';

// Preloaders
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';

// Utils
import { pageDefinition } from 'site-modules/shared/pages/page';
import { getQuery } from 'client/utils/location';
import { getPageFiltersValues } from 'site-modules/shared/utils/inventory/srp-utils';

import { isCpo } from 'site-modules/shared/utils/inventory-utils/is-cpo';
import { getSrpSeoObject } from 'site-modules/shared/utils/inventory/srp-seo-object';
import { getFilterFromLocation } from 'site-modules/shared/utils/inventory/search-filter';
import { getVehicleFromSrpInventoryData } from 'site-modules/shared/components/native-ad/utils/get-ad-targeting';
import { setAdsListToPreload } from 'site-modules/shared/components/native-ad/utils/utils';
import { getUsurpImages } from 'client/site-modules/shared/utils/inventory/srp-images';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { getCpoMakeContentPath } from 'site-modules/shared/utils/cpo-helper';
import { getMultiMMCriticalKeyValue } from 'site-modules/shared/utils/inventory/multi-make-model';

// Actions
import { updateVehicle } from 'client/actions/vehicle';
import { setCriticalCss } from 'client/actions/critical-css';

// Constants
import { SRP_AD } from 'site-modules/shared/components/native-ad/utils/constants';

// Components
import { EdmundsCpaNoAdhesionDecorator } from 'client/site-modules/shared/pages/decorators/edmunds-cpa-no-adhesion';
import { CarBuyingOnlineContainer } from './car-buying-online-container';

const DESKTOP_NUM_CARDS = 4;
const MOBILE_NUM_CARDS = 2;

export function getCriticalCssKeys({
  hasSuggestedFacets = false,
  inventoryResultsAboveFold = [],
  disableInventoryMLPhotoSort,
  isMobile = false,
  multiMM = false,
}) {
  const hasMultiplePhotosAboveFold = inventoryResultsAboveFold.some(
    vehicle => getUsurpImages(vehicle, disableInventoryMLPhotoSort).photoUrls.length > 1
  );

  // 2 * 2 * 3 = 12 values
  const keys = {
    sfacet: hasSuggestedFacets, // 2 values (true, false)
    hasMultiplePhotosAboveFold, // 2 values
    multiMM, // 3 values for desktop (select, addAnother, bubbles); 1 value for mobile (false)
  };

  if (isMobile) {
    /*
      TODO: delete criticalCssHeight (and clean up keys) after https://edmunds.atlassian.net/browse/PLAT-2314 is merged

      Increasing the critical css height for mobile to confidently get 2 cards into view for all scenarios

      We chose this number to give additional padding for 2 full cards.  This number is closer to fitting
      3 cards.  We have some scenarios where we have a large zero box as well as some line-wrapping
      that can start pushing cards closer and closer to the critical css bounds.

      Example: https://www.edmunds.com/used-certified-pre-owned-toyota-camry/?test-zip=90401

      For this example above, we need 1280 pixels to fit two cards.  However, for safety, we would like to
      add a third card, which takes us to 1750 pixels.

      While this might seem like a lot of extra padding, minimal css will be added because the
      cards are overall very similar.  If we were on a page with a lot of different content, we might
      use smaller height, but since these cards are so similar, the risk doesn't outweigh the reward.
    */
    keys.criticalCssHeight = 1750;
  } else {
    /*
      TODO: delete criticalCssHeight (and clean up keys) after https://edmunds.atlassian.net/browse/PLAT-2314 is merged

      For desktop, we are increasing the height to 1300 so that the "Price and Payment Filter" is within the viewport.
      This filter causes a CLS when user scrolls before the non-critical css is loaded.
    */
    keys.criticalCssHeight = 1300;
  }

  return keys;
}

export const getPreload = (store, props) => {
  // // Predefine/Preload data required for page start
  let preloader = null;

  preloader = new ModelPreloader(store);

  const modelState = getReduxModelStateFromStore(store);
  const urlContext = modelState.get(URL_CONTEXT_PATH, UrlModel);

  const state = store.getState();
  const query = getQuery(props.location);
  // Prepare filters for usage
  let pageFilters = getFilterFromLocation(query, urlContext);

  if (query.wz) {
    pageFilters = {
      ...pageFilters,
      wz: query.wz,
    };
  }
  if (query.deliveryFee) {
    pageFilters = {
      ...pageFilters,
      deliveryFee: query.deliveryFee,
    };
  }

  const { selectedMake, inventoryType } = getPageFiltersValues(modelState, pageFilters);

  pageFilters = { ...pageFilters, isBuyOnline: true, fetchSuggestedFacets: true };

  preloader.set('searchResultsFilter', InventoryModel, pageFilters);
  preloader.resolve('searchResults', InventoryModel);

  const isPrequalified = get(state, 'financing.prequalificationData');

  return preloader.load().then(stateModel => {
    const inventoryData = stateModel.get('searchResults', InventoryModel);
    const inventoryResults = get(inventoryData, 'inventories.results', []);
    const suggestedFacets = get(inventoryData, 'suggestedFacets', []);
    const firstType = get(inventoryType, '[0]', '');
    const isOnlyType = inventoryType.length === 1;

    // Resolve available make with certified program
    const isSelectedMakeCPO = isOnlyType && isCpo(firstType) && selectedMake;

    if (isSelectedMakeCPO) {
      preloader.resolve(`content["${getCpoMakeContentPath(makeNiceName(selectedMake))}"]`, CmsModel);
    }

    const vehicleData = getVehicleFromSrpInventoryData(inventoryData, pageFilters);
    const vehiclePageData = vehicleData.vehiclePageData;
    const vehicleAdData = vehicleData.vehicleAdData;
    store.dispatch(updateVehicle(vehiclePageData));

    if (selectedMake) {
      // when page is make-srp type, vehicleAdData is defined otherwise is null
      setAdsListToPreload(preloader, store.getState(), [SRP_AD.AD_NAME], vehicleAdData || vehiclePageData);
      preloader.resolve(InventoryPaths.getAdTargeting(), InventoryModel);
    }

    /**
     * enable critical css when there is:
     *
     *   1. has search results cards
     *   2. at least one filter is selected
     *   3. is not "prequalified" for capital one
     */
    const enableCriticalCss =
      !!get(inventoryData, 'inventories.results.length') && !isEmpty(pageFilters) && !isPrequalified;

    // for desktop = 4
    // for mobile = 2
    const numCardsAboveFold = state.mobile ? MOBILE_NUM_CARDS : DESKTOP_NUM_CARDS;
    const disableInventoryMLPhotoSort = modelState.get('disable-inventory-ml-photo-sort', FeatureFlagModel);

    const criticalCssKeys = enableCriticalCss
      ? getCriticalCssKeys({
          type: isOnlyType ? firstType : '',
          hasSuggestedFacets: suggestedFacets.length > 0,
          inventoryResultsAboveFold: inventoryResults.slice(0, numCardsAboveFold),
          disableInventoryMLPhotoSort,
          isMobile: state.mobile,
          multiMM:
            !state.mobile &&
            getMultiMMCriticalKeyValue({
              makesNum: get(pageFilters, 'make', []).length,
              modelsNum: get(pageFilters, 'model', []).length,
            }),
        })
      : false;

    store.dispatch(setCriticalCss(criticalCssKeys));

    const firstInventory = get(inventoryData, 'inventories.results[0]');

    if (firstInventory) {
      const { photoUrls } = getUsurpImages(firstInventory, disableInventoryMLPhotoSort);
      const imageLink = get(photoUrls, '[0]');

      const preloadHeroImageAsset = {
        href: imageLink,
        as: 'image',
      };

      preloader.set('server.preloadedAssets', PageModel, [preloadHeroImageAsset]);
    }

    return preloader.load();
  });
};

export const pageDefinitionConfiguration = {
  name: 'used_car_buying_online_inventory_srp_ntl',
  category: 'car_inventory',
  seo: (state, props) => ({
    ...getSrpSeoObject(state, props),
    robots: {
      noIndex: true,
      noFollow: true,
    },
  }),
  preload: getPreload,
  component: CarBuyingOnlineContainer,
  decorator: EdmundsCpaNoAdhesionDecorator,
  options: {
    disableScrollToTop: true,
    hasVehicleContext: true,
    vehicleContextChange: true,
    disableAdRefreshOnScroll: true,
  },
};

export default pageDefinition(pageDefinitionConfiguration);
